// SPDX-License-Identifier: MIT
// Copyright (c) 2020-2023 The Pybricks Authors

import './index.scss';
import { HotkeysProvider, OverlayToaster } from '@blueprintjs/core';
import { configureStore } from '@reduxjs/toolkit';
import { I18nContext } from '@shopify/react-i18n';
import React from 'react';
import { OverlayProvider } from 'react-aria';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { createLogger } from 'redux-logger';
import createSagaMiddleware from 'redux-saga';
import { appVersion } from './app/constants';
import AppShell from './cloud/AppShell';
import Dashboard from './cloud/Dashboard';
import ProjectPage from './cloud/ProjectPage';
import { debugMiddleware } from './cloud/debug';
import { applyPreferences } from './cloud/preferences';
import { cloudMiddleware } from './cloud/useProjectFiles';
import { db } from './fileStorage/context';
import { i18nManager } from './i18n';
import { rootReducer } from './reducers';
import { serializableCheck } from './redux';
import reportWebVitals from './reportWebVitals';
import rootSaga, { RootSagaContext } from './sagas';
import { defaultTerminalContext } from './terminal/TerminalContext';
import { defined } from './utils';
import { createCountFunc } from './utils/iter';

const toasterRef = React.createRef<OverlayToaster>();

const sagaMiddleware = createSagaMiddleware<RootSagaContext>({
    context: {
        nextMessageId: createCountFunc(),
        terminal: defaultTerminalContext,
        fileStorage: db,
        toasterRef,
    },
});

// TODO: add runtime option or filter - logger affects firmware flash performance
const loggerMiddleware = createLogger({ predicate: () => false });

const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({ serializableCheck })
            .concat(sagaMiddleware)
            .concat(cloudMiddleware)
            .concat(debugMiddleware)
            .concat(loggerMiddleware),
});

// special styling for beta versions
if (appVersion.match(/beta/)) {
    document.body.classList.add('pb-beta');
}

// prevent default drag/drop which just "downloads" any file dropped anywhere
// in the browser window

const dragEventHandler = (e: DragEvent) => {
    if (
        e.target instanceof Element &&
        !e.target.classList.contains('pb-dropzone-root')
    ) {
        e.preventDefault();

        if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = 'none';
            e.dataTransfer.dropEffect = 'none';
        }
    }
};

window.addEventListener('dragenter', dragEventHandler, false);
window.addEventListener('dragover', dragEventHandler);
window.addEventListener('drop', dragEventHandler);

applyPreferences();

sagaMiddleware.run(rootSaga);

const container = document.getElementById('root');
defined(container);
const root = createRoot(container);

// The dashboard is the entry point, and the editor lives under a project, so
// that a project can be linked to and reopened.
//
// Navigating away from a project unmounts the editor, which disposes its
// monaco widget. The editor saga starts with the app and binds to each widget
// monaco creates without releasing the last, so coming back leaves an old
// handler writing to a disposed widget: files load and appear in the explorer,
// but the editor pane stays empty. Keeping the whole app in one route means
// the editor is created once and stays.
const router = createBrowserRouter([
    {
        element: <AppShell />,
        children: [
            { path: '/', element: <Dashboard /> },
            { path: '/project/:slug', element: <ProjectPage /> },
            // a mistyped or stale link; the dashboard is the way back
            { path: '*', element: <Dashboard /> },
        ],
    },
]);

root.render(
    <Provider store={store}>
        <I18nContext.Provider value={i18nManager}>
            <OverlayProvider>
                <HotkeysProvider>
                    <RouterProvider router={router} />
                </HotkeysProvider>
            </OverlayProvider>
            <OverlayToaster ref={toasterRef} />
        </I18nContext.Provider>
    </Provider>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
